import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  TabsNavWrapper,
  TabsNavContainer,
  TabNavItem,
  TabNavLabel,
  TabBadge,
} from "./styles";

export interface PageTabItem<T extends string = string> {
  id: T;
  label: string;
  icon?: React.ReactNode;
  badge?: string;
  badgeVariant?: "default" | "success" | "neutral";
}

interface PageTabsProps<T extends string = string> {
  tabs: PageTabItem<T>[];
  activeTab: T;
  onChange: (tabId: T) => void;
}

export function PageTabs<T extends string = string>({
  tabs,
  activeTab,
  onChange,
}: PageTabsProps<T>) {
  const tabsNavRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const hasDragged = useRef(false);

  const checkTabsScroll = useCallback(() => {
    const el = tabsNavRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 2);
  }, []);

  useEffect(() => {
    const el = tabsNavRef.current;
    if (!el) return;
    checkTabsScroll();

    const handleResize = () => checkTabsScroll();
    window.addEventListener("resize", handleResize);
    el.addEventListener("scroll", checkTabsScroll, { passive: true });

    return () => {
      window.removeEventListener("resize", handleResize);
      el.removeEventListener("scroll", checkTabsScroll);
    };
  }, [checkTabsScroll, tabs]);

  useEffect(() => {
    const el = tabsNavRef.current;
    if (!el) return;
    const activeEl = el.querySelector<HTMLElement>('[data-active="true"]');
    if (activeEl) {
      activeEl.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [activeTab]);

  const handleMouseDown = (e: React.MouseEvent) => {
    const el = tabsNavRef.current;
    if (!el) return;
    setIsDragging(true);
    hasDragged.current = false;
    dragStartX.current = e.pageX - el.offsetLeft;
    dragScrollLeft.current = el.scrollLeft;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const el = tabsNavRef.current;
    if (!el) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = (x - dragStartX.current) * 1.5;
    if (Math.abs(walk) > 4) {
      hasDragged.current = true;
    }
    el.scrollLeft = dragScrollLeft.current - walk;
  };

  const handleMouseUpOrLeave = () => {
    setIsDragging(false);
  };

  return (
    <TabsNavWrapper
      ref={tabsNavRef}
      $canScrollLeft={canScrollLeft}
      $canScrollRight={canScrollRight}
      $isDragging={isDragging}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUpOrLeave}
      onMouseLeave={handleMouseUpOrLeave}
    >
      <TabsNavContainer>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <TabNavItem
              key={tab.id}
              type="button"
              data-active={isActive}
              $active={isActive}
              onClick={() => {
                if (hasDragged.current) return;
                onChange(tab.id);
              }}
            >
              {tab.icon}
              <TabNavLabel>{tab.label}</TabNavLabel>
              {tab.badge && (
                <TabBadge $variant={tab.badgeVariant}>{tab.badge}</TabBadge>
              )}
            </TabNavItem>
          );
        })}
      </TabsNavContainer>
    </TabsNavWrapper>
  );
}
